import React, { useState, useRef } from 'react'

const use3DCard = (intensity = 'normal') => {
  const [transform, setTransform] = useState('')
  const [isHovered, setIsHovered] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return

    const card = cardRef.current
    const rect = card.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2

    let maxRotation: number
    let scaleValue: number
    switch (intensity) {
      case 'subtle':
        maxRotation = 8
        scaleValue = 1.02
        break
      case 'normal':
        maxRotation = 15
        scaleValue = 1.05
        break
      case 'strong':
        maxRotation = 20
        scaleValue = 1.08
        break
      default:
        maxRotation = 15
        scaleValue = 1.05
    }

    const rotateX = ((y - centerY) / centerY) * -maxRotation
    const rotateY = ((x - centerX) / centerX) * maxRotation

    const lightX = ((x - centerX) / centerX) * 50 + 50
    const lightY = ((y - centerY) / centerY) * 50 + 50

    const newTransform = `
      rotateX(${rotateX}deg) 
      rotateY(${rotateY}deg) 
      scale3d(${scaleValue}, ${scaleValue}, ${scaleValue})
    `

    setTransform(newTransform)
    card.style.setProperty('--mouse-x', `${lightX}%`)
    card.style.setProperty('--mouse-y', `${lightY}%`)
  }

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
    setTransform('rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)')
    if (cardRef.current) {
      cardRef.current.style.setProperty('--mouse-x', '50%')
      cardRef.current.style.setProperty('--mouse-y', '50%')
    }
  }

  return {
    cardRef,
    transform,
    isHovered,
    handleMouseMove,
    handleMouseEnter,
    handleMouseLeave,
  }
}

interface Card3DProps {
  children: React.ReactNode
  className?: string
  intensity?: 'subtle' | 'normal' | 'strong'
  style?: React.CSSProperties
  [key: string]: any
}

const Card3D = ({ children, className = '', intensity = 'normal', style = {}, ...props }: Card3DProps) => {
  const { cardRef, transform, isHovered, handleMouseMove, handleMouseEnter, handleMouseLeave } = use3DCard(intensity)

  return (
    // 注意:不要在这里(尤其是带 overflow-hidden 的内层)用 transform-style: preserve-3d。
    // Safari/WebKit 对「preserve-3d + overflow:hidden + backdrop-blur」的层做命中测试会失效,
    // 卡片里的按钮点击全部落到这个外层容器上——/fushiji 定价卡「立即订购」在 Safari 点不动就是这个原因。
    // 子元素没有 translateZ,平面 3D 倾斜(rotateX/Y + perspective)不需要 preserve-3d。
    <div ref={cardRef} className="relative" style={{ perspective: '1000px' }} onMouseMove={handleMouseMove} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        className={`relative cursor-pointer overflow-hidden rounded-2xl border border-white/30 shadow-lg transition-all duration-300 ease-out ${className}`}
        style={{
          transform,
          // @ts-ignore
          '--mouse-x': '50%',
          '--mouse-y': '50%',
          ...style,
        }}
        {...props}
      >
        {/* 鼠标跟踪光照效果 */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300"
          style={{
            background: isHovered ? `radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255,255,255,0.1) 0%, transparent 50%)` : 'transparent',
            opacity: isHovered ? 1 : 0,
          }}
        />

        {/* 彩色渐变背景 */}
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 transition-opacity duration-300"
          style={{
            background: isHovered ? `linear-gradient(135deg, rgba(255,64,83,0.3) 0%, transparent 50%, rgba(0,160,233,0.3) 100%)` : 'transparent',
            opacity: isHovered ? 0.5 : 0,
          }}
        />

        {/* 内容区域 */}
        <div className="relative z-10">{children}</div>
      </div>
    </div>
  )
}

interface ProductImage3DProps {
  src: string
  alt: string
  width?: number | string
  height?: number | string
  className?: string
  showOverlay?: boolean
  intensity?: 'subtle' | 'normal' | 'strong'
}

const ProductImage3D = ({ src, alt, width, height, className = '', showOverlay = false, intensity = 'normal' }: ProductImage3DProps) => {
  const { cardRef, transform, isHovered, handleMouseMove, handleMouseEnter, handleMouseLeave } = use3DCard(intensity)

  return (
    <div
      ref={cardRef}
      className="group relative"
      style={{
        perspective: '1000px',
      }}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className={`hover:shadow-3xl relative cursor-pointer overflow-hidden rounded-3xl bg-white/90 p-4 shadow-2xl transition-all duration-500 ease-out ${className}`}
        style={{
          transform,
          // @ts-ignore
          '--mouse-x': '50%',
          '--mouse-y': '50%',
          ...(typeof width === 'string' ? {} : { width }),
          ...(typeof height === 'string' ? {} : { height }),
        }}
      >
        <img
          src={src}
          alt={alt}
          className={`h-full w-full rounded-2xl transition-all duration-300 ${className}`}
          style={{
            objectFit: 'cover',
            transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          }}
        />

        {/* 场景图片的标题覆盖层 */}
        {showOverlay && (
          <>
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <div className="rounded-lg bg-white/90 px-4 py-2 backdrop-blur-sm">
                <p className="font-semibold text-gray-800">{alt}</p>
              </div>
            </div>
          </>
        )}

        <div
          className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300"
          style={{
            background: isHovered ? `radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(255,255,255,0.15) 0%, transparent 50%)` : 'transparent',
            opacity: isHovered ? 1 : 0,
          }}
        />

        <div
          className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300"
          style={{
            background: isHovered ? `linear-gradient(135deg, rgba(255,64,83,0.2) 0%, transparent 50%, rgba(0,160,233,0.2) 100%)` : 'transparent',
            opacity: isHovered ? 0.6 : 0,
          }}
        />

        <div
          className="absolute -bottom-8 left-1/2 h-4 w-3/4 -translate-x-1/2 rounded-full bg-black/20 blur-xl transition-all duration-300"
          style={{
            opacity: isHovered ? 0.4 : 0.2,
            transform: `translateX(-50%) ${isHovered ? 'scale(1.2)' : 'scale(1)'}`,
          }}
        />
      </div>
    </div>
  )
}

export default Card3D
export { ProductImage3D, use3DCard }
